import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  BuildingOfficeIcon,
  ClockIcon,
  EyeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import OpenStreetMap, { ReportPoint } from './OpenStreetMap';
import PageHeader from './ui/PageHeader';
import StatCard from './ui/StatCard';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Modal from './ui/Modal';

interface TechnicianLocation {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  role: string;
  departmentId: string;
  departmentName: string;
  phone: string;
  latitude: number;
  longitude: number;
  lastUpdated: string;
  status: 'online' | 'offline' | 'busy' | 'available';
  currentAssignment?: {
    id: string;
    reportTitle: string;
    status: string;
    priority: string;
  };
  isActive: boolean;
}

const TECH_STATUS_TONE: Record<string, 'success' | 'warning' | 'info' | 'neutral'> = {
  available: 'success',
  busy: 'warning',
  online: 'info',
  offline: 'neutral',
};

const OPEN_FAULT_STATUSES = new Set(['submitted', 'acknowledged', 'assigned', 'in_progress', 'reopened', 'escalated']);

const Map: React.FC = () => {
  const [technicians, setTechnicians] = useState<TechnicianLocation[]>([]);
  const [reports, setReports] = useState<ReportPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState<TechnicianLocation | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchTechnicians();
    fetchReports();

    const interval = setInterval(() => {
      if (autoRefresh) {
        fetchTechnicians();
        fetchReports();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchReports = async () => {
    try {
      const response = await apiService.get('/reports/map-points');
      const data = response.data;
      if (data.success && Array.isArray(data.data)) {
        setReports(data.data);
      }
    } catch (err) {
      console.error('Error fetching report positions:', err);
    }
  };

  const fetchTechnicians = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch real data from the API
      try {
        const response = await apiService.get('/technician-locations');
        const data = response.data;
        
        console.log('Technician locations API response:', data);
        
        if (data.success && data.data && data.data.length > 0) {
          // Transform the API data to match our interface
          const apiTechnicians = data.data.map((tech: any) => ({
            id: tech.id,
            userId: tech.user_id,
            firstName: tech.first_name || 'Unknown',
            lastName: tech.last_name || 'User',
            role: tech.role || 'municipal_staff',
            departmentId: tech.department_id || '',
            departmentName: tech.department_name || 'Unknown Department',
            phone: tech.phone || 'No phone',
            latitude: parseFloat(tech.latitude) || -29.8587,
            longitude: parseFloat(tech.longitude) || 31.0218,
            lastUpdated: tech.last_updated || new Date().toISOString(),
            status: tech.status || 'offline',
            currentAssignment: tech.current_assignment || null,
            isActive: tech.is_active !== false
          }));
          
          console.log('Transformed technicians:', apiTechnicians);
          setTechnicians(apiTechnicians);
          return; // Exit early if we have real data
        } else {
          setTechnicians([]);
          return;
        }
      } catch (apiError) {
        console.error('Technician locations API failed:', apiError);
        setTechnicians([]);
        setError('Technician locations are currently unavailable');
        return;
      }

      // Use mock data if API fails or returns no data
      const mockTechnicians: TechnicianLocation[] = [
        {
          id: '1',
          userId: 'user1',
          firstName: 'John',
          lastName: 'Technician',
          role: 'municipal_staff',
          departmentId: 'dept1',
          departmentName: 'Water & Sanitation',
          phone: '+27 82 123 4567',
          latitude: -29.4858, // Howick CBD
          longitude: 30.2306,
          lastUpdated: new Date().toISOString(),
          status: 'busy',
          currentAssignment: {
            id: 'assign1',
            reportTitle: 'Broken Water Pipe on Main Street, Howick',
            status: 'in_progress',
            priority: 'high'
          },
          isActive: true
        },
        {
          id: '2',
          userId: 'user2',
          firstName: 'Sarah',
          lastName: 'Engineer',
          role: 'department_head',
          departmentId: 'dept2',
          departmentName: 'Roads & Infrastructure',
          phone: '+27 82 234 5678',
          latitude: -29.4872, // Howick Industrial Area
          longitude: 30.2289,
          lastUpdated: new Date().toISOString(),
          status: 'available',
          isActive: true
        },
        {
          id: '3',
          userId: 'user3',
          firstName: 'David',
          lastName: 'Maintenance',
          role: 'municipal_staff',
          departmentId: 'dept3',
          departmentName: 'Electricity',
          phone: '+27 82 345 6789',
          latitude: -29.4845, // Howick Residential Area
          longitude: 30.2321,
          lastUpdated: new Date().toISOString(),
          status: 'online',
          currentAssignment: {
            id: 'assign2',
            reportTitle: 'Street Light Outage on Victoria Road',
            status: 'assigned',
            priority: 'medium'
          },
          isActive: true
        },
        {
          id: '4',
          userId: 'user4',
          firstName: 'Maria',
          lastName: 'Inspector',
          role: 'municipal_staff',
          departmentId: 'dept4',
          departmentName: 'Building Control',
          phone: '+27 82 456 7890',
          latitude: -29.4861, // Howick Shopping Centre
          longitude: 30.2298,
          lastUpdated: new Date().toISOString(),
          status: 'offline',
          isActive: true
        },
        {
          id: '5',
          userId: 'user5',
          firstName: 'Peter',
          lastName: 'Technician',
          role: 'municipal_staff',
          departmentId: 'dept1',
          departmentName: 'Water & Sanitation',
          phone: '+27 82 567 8901',
          latitude: -29.4839, // Howick Suburb
          longitude: 30.2315,
          lastUpdated: new Date().toISOString(),
          status: 'busy',
          currentAssignment: {
            id: 'assign3',
            reportTitle: 'Sewer Blockage in Residential Area',
            status: 'in_progress',
            priority: 'critical'
          },
          isActive: true
        }
      ];
      setTechnicians(mockTechnicians);
    } catch (err) {
      console.error('Error fetching technicians:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch technician locations');
      
      // Fallback to mock data on error
      const mockTechnicians: TechnicianLocation[] = [
        {
          id: '1',
          userId: 'user1',
          firstName: 'John',
          lastName: 'Technician',
          role: 'municipal_staff',
          departmentId: 'dept1',
          departmentName: 'Water & Sanitation',
          phone: '+27 82 123 4567',
          latitude: -29.4858, // Howick CBD
          longitude: 30.2306,
          lastUpdated: new Date().toISOString(),
          status: 'busy',
          currentAssignment: {
            id: 'assign1',
            reportTitle: 'Broken Water Pipe on Main Street, Howick',
            status: 'in_progress',
            priority: 'high'
          },
          isActive: true
        },
        {
          id: '2',
          userId: 'user2',
          firstName: 'Sarah',
          lastName: 'Engineer',
          role: 'department_head',
          departmentId: 'dept2',
          departmentName: 'Roads & Infrastructure',
          phone: '+27 82 234 5678',
          latitude: -29.4872, // Howick Industrial Area
          longitude: 30.2289,
          lastUpdated: new Date().toISOString(),
          status: 'available',
          isActive: true
        },
        {
          id: '3',
          userId: 'user3',
          firstName: 'David',
          lastName: 'Maintenance',
          role: 'municipal_staff',
          departmentId: 'dept3',
          departmentName: 'Electricity',
          phone: '+27 82 345 6789',
          latitude: -29.4845, // Howick Residential Area
          longitude: 30.2321,
          lastUpdated: new Date().toISOString(),
          status: 'online',
          currentAssignment: {
            id: 'assign2',
            reportTitle: 'Street Light Outage on Victoria Road',
            status: 'assigned',
            priority: 'medium'
          },
          isActive: true
        },
        {
          id: '4',
          userId: 'user4',
          firstName: 'Maria',
          lastName: 'Inspector',
          role: 'municipal_staff',
          departmentId: 'dept4',
          departmentName: 'Building Control',
          phone: '+27 82 456 7890',
          latitude: -29.4861, // Howick Shopping Centre
          longitude: 30.2298,
          lastUpdated: new Date().toISOString(),
          status: 'offline',
          isActive: true
        },
        {
          id: '5',
          userId: 'user5',
          firstName: 'Peter',
          lastName: 'Technician',
          role: 'municipal_staff',
          departmentId: 'dept1',
          departmentName: 'Water & Sanitation',
          phone: '+27 82 567 8901',
          latitude: -29.4839, // Howick Suburb
          longitude: 30.2315,
          lastUpdated: new Date().toISOString(),
          status: 'busy',
          currentAssignment: {
            id: 'assign3',
            reportTitle: 'Sewer Blockage in Residential Area',
            status: 'in_progress',
            priority: 'critical'
          },
          isActive: true
        }
      ];
      setTechnicians(mockTechnicians);
    } finally {
      setIsLoading(false);
    }
  };



  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case 'available': return 'Available';
      case 'busy': return 'Busy';
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      default: return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Real-Time Staff Tracking"
        subtitle="Monitor the location and status of all municipal staff and technicians"
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Auto-refresh
            </label>
            <Button variant="secondary" icon={ArrowPathIcon} onClick={fetchTechnicians}>
              Refresh
            </Button>
          </>
        }
      />

      {error && (
        <div className="mb-4 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard label="Total Staff" value={technicians.length} icon={UserIcon} tone="blue" />
        <StatCard
          label="Available"
          value={technicians.filter((t) => t.status === 'available').length}
          icon={UserIcon}
          tone="emerald"
        />
        <StatCard
          label="Busy"
          value={technicians.filter((t) => t.status === 'busy').length}
          icon={ExclamationTriangleIcon}
          tone="amber"
        />
        <StatCard
          label="Departments"
          value={new Set(technicians.map((t) => t.departmentId)).size}
          icon={BuildingOfficeIcon}
          tone="purple"
        />
        <StatCard
          label="Open Faults"
          value={reports.filter((r) => OPEN_FAULT_STATUSES.has(r.status)).length}
          icon={ExclamationTriangleIcon}
          tone="red"
        />
      </div>

      {/* Map and Sidebar */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Map */}
            <div className="lg:col-span-2">
              <div className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Live Map</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Real-time location of municipal staff, technicians and reported faults
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-3 w-3 rounded-full bg-primary-600 border border-white shadow" />
                      Staff
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-2.5 w-2.5 rotate-45 bg-red-500 border border-white shadow" />
                      Fault
                    </span>
                  </div>
                </div>
                        <OpenStreetMap
          technicians={technicians}
          onTechnicianSelect={setSelectedTechnician}
          selectedTechnicianId={selectedTechnician?.id}
          reports={reports}
        />
              </div>
            </div>

            {/* Staff List */}
            <div className="lg:col-span-1">
              <div className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Staff List</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {technicians.length} staff members
                  </p>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {technicians.map((technician) => (
                    <div
                      key={technician.id}
                      className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedTechnician?.id === technician.id ? 'bg-primary-50 border-primary-200' : ''
                      }`}
                      onClick={() => setSelectedTechnician(technician)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">
                              {technician.firstName} {technician.lastName}
                            </h4>
                            <Badge tone={technician.isActive ? 'success' : 'neutral'}>
                              {technician.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>

                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            {technician.role} • {technician.departmentName}
                          </p>

                          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                            <Badge tone={TECH_STATUS_TONE[technician.status] ?? 'neutral'}>
                              {getStatusDisplayName(technician.status)}
                            </Badge>

                            <span className="flex items-center">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              {new Date(technician.lastUpdated).toLocaleTimeString()}
                            </span>
                          </div>

                          {technician.currentAssignment && (
                            <div className="mt-2 rounded-sm border border-primary-200 bg-primary-50 p-2">
                              <p className="text-xs font-medium text-primary-800">
                                Current Assignment: {technician.currentAssignment.reportTitle}
                              </p>
                              <p className="text-xs text-primary-600">
                                Status: {technician.currentAssignment.status} • Priority: {technician.currentAssignment.priority}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

      {/* Selected Technician Details Modal */}
      <Modal
        open={!!selectedTechnician}
        onClose={() => setSelectedTechnician(null)}
        title="Staff details"
        footer={
          selectedTechnician && (
            <>
              <Button variant="secondary" icon={EyeIcon} onClick={() => { window.location.href = '/users'; }}>
                View profile
              </Button>
              <Button variant="primary" icon={EyeIcon} onClick={() => { window.location.href = '/reports'; }}>
                View reports
              </Button>
            </>
          )
        }
      >
        {selectedTechnician && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className={`h-3 w-3 rounded-full ${selectedTechnician.isActive ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {selectedTechnician.firstName} {selectedTechnician.lastName}
              </span>
            </div>

            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <p><span className="font-medium">Role:</span> {selectedTechnician.role}</p>
              <p><span className="font-medium">Department:</span> {selectedTechnician.departmentName}</p>
              <p><span className="font-medium">Status:</span> {getStatusDisplayName(selectedTechnician.status)}</p>
              <p><span className="font-medium">Phone:</span> {selectedTechnician.phone}</p>
              <p><span className="font-medium">Last updated:</span> {new Date(selectedTechnician.lastUpdated).toLocaleString()}</p>
              <p><span className="font-medium">Location:</span> {selectedTechnician.latitude.toFixed(6)}, {selectedTechnician.longitude.toFixed(6)}</p>
            </div>

            {selectedTechnician.currentAssignment && (
              <div className="rounded-sm border border-primary-200 bg-primary-50 p-3">
                <h4 className="mb-2 font-medium text-primary-900">Current assignment</h4>
                <p className="text-sm text-primary-800">{selectedTechnician.currentAssignment.reportTitle}</p>
                <div className="mt-2 flex items-center space-x-4 text-xs text-primary-600">
                  <span>Status: {selectedTechnician.currentAssignment.status}</span>
                  <span>Priority: {selectedTechnician.currentAssignment.priority}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Map;
